/**
 * Vehicle photo renditions — one contract for the pipeline that WRITES them and
 * every surface that READS them.
 *
 * The image pipeline never upscales: a source narrower than a given width simply
 * doesn't get that rendition. Callers that hardcode a size therefore render a
 * broken <img> for any photo smaller than it — a 500px upload has no `-640.webp`
 * at all. Pick the width through `photoRenditionWidth` instead of assuming.
 */

/** Widths the pipeline attempts, ascending. Source of truth for both sides. */
export const PHOTO_SIZES = [240, 640, 1280, 1920] as const;

export type PhotoSize = (typeof PHOTO_SIZES)[number];

/**
 * The best rendition width that actually exists for a photo:
 * the largest generated size that doesn't exceed `desired`, or — when the source
 * is smaller than everything requested — the largest one that was generated.
 *
 * `sourceWidth` is the original image width stored on the photo descriptor.
 */
export function photoRenditionWidth(sourceWidth: number, desired: number): PhotoSize {
  // Anything the pipeline could have produced for this source.
  const generated = PHOTO_SIZES.filter((s) => s <= sourceWidth);
  // A source narrower than the smallest size is rejected at upload (the pipeline
  // throws "no renditions produced"), so this fallback only guards legacy rows.
  const available = generated.length > 0 ? generated : [PHOTO_SIZES[0]];
  const withinBudget = available.filter((s) => s <= desired);
  return withinBudget.length > 0 ? withinBudget[withinBudget.length - 1]! : available[0]!;
}

/** Public URL of the best WebP rendition for `desired` display width. */
export function photoUrl(publicBase: string, photo: { key: string; width: number }, desired: number): string {
  return `${publicBase}/${photo.key}-${photoRenditionWidth(photo.width, desired)}.webp`;
}

/**
 * Move `key` to `position` and renumber, returning a NEW array.
 *
 * Remove-then-insert, not "set the field and re-sort": sorting leaves the moved
 * photo tied with whatever already held that position, and `Array#sort` is
 * stable, so the incumbent always won and a photo could never be promoted to
 * position 0 — the cover was unchangeable.
 */
export function reorderPhotos<T extends { key: string; position: number }>(
  photos: T[],
  key: string,
  position: number,
): T[] {
  const ordered = [...photos].sort((a, b) => a.position - b.position);
  const moving = ordered.find((p) => p.key === key);
  if (!moving) return photos;
  const rest = ordered.filter((p) => p.key !== key);
  const at = Math.min(Math.max(position, 0), rest.length);
  rest.splice(at, 0, moving);
  return rest.map((p, i) => ({ ...p, position: i }));
}
